import { useState } from "react";
import { Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSetPersona, type AddablePersona } from "@/entities/persona";
import { PersonaPicker } from "./PersonaPicker";

interface AddPersonaButtonProps {
  jobId: number;
  addable: AddablePersona[];
  /** "chip" — dashed pill for the persona strip. "row" — flat line for the sidebar tree. */
  variant: "chip" | "row";
  /** Which side of the trigger the panel opens on: "below" for the persona strip,
   *  "right" for the sidebar tree. Radix flips it automatically when there's no room. */
  placement?: "below" | "right";
  /** Fired before the mutation — the sidebar uses it to open the project first. */
  onBeforeAdd?: () => void;
}

const TRIGGER_CLASSES: Record<AddPersonaButtonProps["variant"], string> = {
  chip: "inline-flex items-center gap-1.5 rounded-full border border-dashed border-primary/60 px-3 py-1 text-sm font-medium text-primary hover:bg-primary/5 transition-colors",
  row: "flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] text-primary hover:bg-primary/5 transition-colors",
};

/** The entry point for adding a persona from the strip's chip and the sidebar's row:
 *  owns the popover state and the mutation, so those two places share one behaviour.
 *  `PersonaInvite` (the single-persona-project card) is a third entry point with its
 *  own suggestion buttons — it calls the same `useSetPersona` mutation directly from
 *  ProjectView rather than through this component.
 *  Renders nothing when there is no role left to add. */
export function AddPersonaButton({
  jobId,
  addable,
  variant,
  placement = "below",
  onBeforeAdd,
}: AddPersonaButtonProps) {
  const [open, setOpen] = useState(false);
  const setPersona = useSetPersona();

  if (!addable.some((role) => !role.generated)) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={variant === "chip" ? "persona-strip-add" : undefined}
          className={TRIGGER_CLASSES[variant]}
        >
          <Plus className={variant === "chip" ? "w-3.5 h-3.5" : "w-3 h-3"} />
          Add persona
        </button>
      </PopoverTrigger>

      <PopoverContent
        side={placement === "right" ? "right" : "bottom"}
        align="start"
        className="w-[330px] p-2.5"
      >
        <PersonaPicker
          addable={addable}
          onPick={(roleSlug) => {
            setOpen(false);
            onBeforeAdd?.();
            setPersona.mutate({ jobId, roleSlug });
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
